import { InjectionToken, Type } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { ModalCloseEvent, ResolvedModalConfig } from './modal.types';

/**
 * Reference to an open modal instance.
 *
 * Injected into the **content component** (via `MODAL_REF`) so it can close
 * itself programmatically and react to the close event.
 *
 * @example
 * ```ts
 * // Inside any content component:
 * private readonly modalRef = inject(MODAL_REF) as ModalRef<boolean>;
 *
 * onConfirm(): void {
 *   this.modalRef.close({ reason: 'confirm', data: true });
 * }
 * ```
 */
export class ModalRef<TResult = void> {
  private readonly _closed$ = new Subject<ModalCloseEvent<TResult>>();
  private _isClosed = false;

  public readonly afterClosed$: Observable<ModalCloseEvent<TResult>> = this._closed$.asObservable();

  public close(event: ModalCloseEvent<TResult>): void {
    if (this._isClosed) {
      return;
    }
    this._isClosed = true;
    this._closed$.next(event);
    this._closed$.complete();
  }
}

// ─── Injection Tokens ────────────────────────────────────────────────────────

/**
 * Provides the `ModalRef` to the **content component** rendered inside the modal.
 * Cast to the expected result type: `inject(MODAL_REF) as ModalRef<MyResult>`.
 */
export const MODAL_REF = new InjectionToken<ModalRef<unknown>>('MODAL_REF');

/**
 * Provides the fully resolved `ModalConfig` to the modal shell component.
 * All fields are guaranteed to be defined (defaults applied by the service).
 */
export const MODAL_CONFIG = new InjectionToken<ResolvedModalConfig>('MODAL_CONFIG');

/**
 * Provides the arbitrary data passed via `ModalOpenOptions.data` to the
 * content component. Inject with `{ optional: true }` and cast as needed.
 */
export const MODAL_DATA = new InjectionToken<unknown>('MODAL_DATA');

/**
 * Provides the `Type<unknown>` of the component to render inside the modal
 * shell. Consumed internally by `ModalComponent` — do not use in content components.
 */
export const MODAL_CONTENT_COMPONENT = new InjectionToken<Type<unknown>>(
  'MODAL_CONTENT_COMPONENT',
);

/** Duration in ms that must match the CSS exit transition of the modal panel. */
export const MODAL_ANIMATION_DURATION_MS = 250;

/** CSS class added to `<body>` while any modal is open. */
export const MODAL_OPEN_BODY_CLASS = 'modal-open' as const;

/** CSS class used by `ModalComponent` to signal the panel is visible/animating-in. */
export const MODAL_IS_OPEN_CLASS = 'is-open' as const;
